import { ErrorMessage } from '@hookform/error-message'
import { Button, Col, Flex, Form, Layout, notification, Radio, Row, Select, Spin, Tooltip, Typography } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { colorBgBrandless, colorBorderDisabled, colorPrimary } from 'src/app/themes/primaryThemeDefault'
import { InfoIcon } from 'src/assets/icons'
import './index.scss'
import { DEFAULT_ALL_ACCOUNTS, IMPORT_PURGE_TYPE, purgeTypeOptions } from './constant'
import React, { useCallback, useEffect, useState } from 'react'
import UploadFileData from './uploadFileData'
import Modal from './Modal/Modal'
import DeleteTransactionsModal from './Modal/DeleteTransactionsModal'
import { debounce } from 'lodash'
import CustomSelect from 'src/app/shared/components/common/select'
import { yupResolver } from '@hookform/resolvers/yup'
import { generateValidationSchema } from './validation'
import { getAccountList, getClientListName } from 'src/app/shared/services/ClientSetupService'
import { getFileDetail, getIssuedcheckFiles, purgeTransaction } from 'src/app/shared/services/TransactionService'
import { IGetFileDetailResponse } from 'src/app/shared/models/Transaction.model'
import { FormValues } from './type'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'

const PurgeTransactions = () => {
  const { t } = useTranslation()
  const { userPermissions } = useRolePermission()
  const [api, contextHolder] = notification.useNotification()
  const [clientList, setClientList] = useState<any>([])
  const [accountList, setAccountList] = useState<any[]>([])
  const [fileList, setFileList] = useState<{ label: string; value: string }[]>([])
  const [fileDetail, setFileDetail] = useState<IGetFileDetailResponse | null>(null)
  // loading status
  const [clientLoading, setClientLoading] = useState<boolean>(false)
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const [fileListLoading, setFileListLoading] = useState<boolean>(false)
  const [detailLoading, setDetailLoading] = useState<boolean>(false)
  const [isPurging, setIsPurging] = useState<boolean>(false)
  const [isShowModal, setIsShowModal] = useState(false)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const defaultValues = {
    clientName: null,
    accountName: null,
    purgeType: IMPORT_PURGE_TYPE.ISSUED_CHECK_FILES,
    checkFile: null,
  }
  const filterOption = (input: string, option?: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields, touchedFields },
    setValue,
    getValues,
    clearErrors,
    reset,
  } = useForm<FormValues>({ mode: 'all', defaultValues, resolver: yupResolver(generateValidationSchema(t)) as any })
  const clientName = useWatch({
    control,
    name: 'clientName',
  })
  const purgeTypeWatch = useWatch({
    control,
    name: 'purgeType',
  })
  const checkFile = useWatch({
    control,
    name: 'checkFile',
  })
  const isFormDisabled = !userPermissions[ESubModules.PURGE_TRANS]?.includes(EPermissions.PURGE_FILE)
  const isDisableButton =
    !!Object.keys(errors)?.length ||
    !Object.keys(dirtyFields)?.length ||
    !Object.keys(touchedFields)?.length ||
    !getValues('accountName') ||
    !getValues('clientName') ||
    !getValues('checkFile') ||
    (fileDetail?.numOfRecords ?? -1) <= 0 ||
    isFormDisabled

  const onSubmit = async () => {
    try {
      if (checkFile) {
        setIsPurging(true)
        const res = await purgeTransaction({
          fileId: checkFile,
        })
        console.log('RES: ', res.data)
        api.success({
          message: t('SystemMessages:PPG03'),
        })
        setIsOpenDeleteModal(false)
        reset()
        setAccountList([])
        setFileDetail(null)
        setFileList([])
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsPurging(false)
    }
  }

  const handleOnchangeClient = (e: string, clear: boolean) => {
    if (clear) {
      setValue('accountName', null)
      clearErrors('accountName')
      setValue('checkFile', null)
      clearErrors('checkFile')
    }
    setAccountLoading(true)
    getAccountList(e).then((res: any) => {
      if (res?.data) {
        const allAccOption = { label: t(DEFAULT_ALL_ACCOUNTS.label), value: DEFAULT_ALL_ACCOUNTS.value }
        const options = res.data.map((item: any) => ({ label: item?.accountName, value: item?.accountId }))
        setAccountList([allAccOption, ...options])
        setAccountLoading(false)
      }
    })
  }

  const debounceSearchStateFn = useCallback(debounce(handleOnchangeClient, 500), [])

  const handleGetFiles = useCallback(
    async ({ clientName, accountName }: { clientName: string; accountName: string }) => {
      try {
        setValue('checkFile', null)
        clearErrors('checkFile')
        setFileListLoading(true)
        const res = await getIssuedcheckFiles({
          accountId: accountName !== DEFAULT_ALL_ACCOUNTS.value ? accountName : null,
          clientId: clientName,
          isAllAccount: accountName === DEFAULT_ALL_ACCOUNTS.value,
          type: purgeTypeWatch,
        })
        if (res.data) {
          setFileList(res.data.map((item) => ({ label: item.value, value: item.fileId })))
        }
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setFileListLoading(false)
      }
    },
    [clearErrors, purgeTypeWatch, setValue],
  )

  const handleGetFileDetail = useCallback(async (id: string) => {
    try {
      setDetailLoading(true)
      const res = await getFileDetail({ id })
      if (res.data) {
        setFileDetail(res.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    setClientLoading(true)
    getClientListName().then((res: any) => {
      if (res?.data) {
        setClientList(
          res?.data?.map((item: any) => ({
            label: item.clientName,
            value: item.id,
          })),
        )

        if (res?.data?.length === 1) {
          setValue('clientName', res?.data[0]?.id)
          debounceSearchStateFn(res?.data[0]?.id, true)
        }
      }
      setClientLoading(false)
    })
  }, [])

  return (
    <Layout.Content style={{ display: 'flex', flexDirection: 'column' }}>
      {contextHolder}
      <Form className='form-container purge-transactions' layout='vertical' onFinish={handleSubmit(onSubmit)}>
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('PurgeTransactions:clientName.label')} className='isRequired'>
              <Controller
                name='clientName'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    style={{ width: '100%' }}
                    maxTagCount='responsive'
                    options={clientList}
                    loading={clientLoading}
                    disabled={clientLoading || isFormDisabled}
                    status={errors?.clientName ? 'error' : ''}
                    placeholder={t('PurgeTransactions:clientName.placeholder')}
                    onChange={(e) => {
                      field.onChange(e)
                      debounceSearchStateFn(e, true)
                    }}
                    filterOption={filterOption}
                    filterSort={(a, b) => a.label.localeCompare(b.label)}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='clientName' />
              </Typography>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('PurgeTransactions:accountName.label')} className='isRequired'>
              <Controller
                name='accountName'
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    showSearch
                    style={{ width: '100%', marginTop: 0 }}
                    maxTagCount='responsive'
                    options={accountList}
                    loading={accountLoading}
                    disabled={accountLoading || isFormDisabled}
                    status={errors?.accountName ? 'error' : ''}
                    placeholder={t('PurgeTransactions:accountName.placeholder')}
                    maxTagPlaceholder={(omittedValues) => {
                      return (
                        <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                          <span>+{omittedValues?.length}</span>
                        </Tooltip>
                      )
                    }}
                    onChange={(e) => {
                      field.onChange(e)
                      handleGetFiles({
                        clientName: clientName ?? '',
                        accountName: e,
                      })
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='accountName' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t('PurgeTransactions:purgeType')}
          className='isRequired label16'
          style={{ height: 22, marginBottom: 8, flex: 1 }}
        />
        <Flex style={{ marginBottom: '24px' }}>
          <Controller
            name='purgeType'
            control={control}
            render={({ field }) => (
              <Radio.Group style={{ display: 'flex', flexDirection: 'column' }} {...field}>
                {purgeTypeOptions.map(({ label, value }) => (
                  <Radio value={value} key={value} disabled={isFormDisabled}>
                    <Typography.Text style={{ fontWeight: 500 }}>{t(label)}</Typography.Text>
                  </Radio>
                ))}
              </Radio.Group>
            )}
          />
          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='purgeType' />
          </Typography>
        </Flex>
        {purgeTypeWatch !== IMPORT_PURGE_TYPE.ALL_TRANSACTION_HISTORY && (
          <Flex
            align='flex-start'
            justify='space-between'
            style={{
              border: `1px solid ${colorBorderDisabled}`,
              padding: '20px',
              borderRadius: '16px',
              marginBottom: 24,
            }}
            vertical
          >
            <Flex
              align='flex-start'
              style={{
                borderColor: colorPrimary,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 12,
                backgroundColor: colorBgBrandless,
                padding: '12px 20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 10,
                width: '100%',
                marginBottom: 24,
              }}
            >
              <InfoIcon width={28} height={28} />
              <Typography.Text style={{ fontWeight: 500 }}>
                {t(`${purgeTypeOptions.find((item) => item.value === purgeTypeWatch)?.info}`)}
              </Typography.Text>
            </Flex>

            {purgeTypeWatch === IMPORT_PURGE_TYPE.ISSUED_CHECK_FILES && (
              <Form.Item
                label={t('PurgeTransactions:dataFileUpload.label')}
                className='isRequired'
                style={{ width: '100%' }}
              >
                <Controller
                  name='checkFile'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      showSearch
                      style={{ width: '100%' }}
                      maxTagCount='responsive'
                      loading={fileListLoading}
                      disabled={fileListLoading || detailLoading || isFormDisabled}
                      options={fileList}
                      status={errors?.checkFile ? 'error' : ''}
                      placeholder={t('PurgeTransactions:dataFileUpload.placeholder')}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        handleGetFileDetail(e)
                      }}
                      filterOption={filterOption}
                      filterSort={(a, b) => a.label.localeCompare(b.label)}
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='checkFile' />
                </Typography>
              </Form.Item>
            )}

            {detailLoading ? (
              <Flex align='center' justify='center' vertical={false} style={{ width: '100%' }}>
                <Spin />
              </Flex>
            ) : (
              <React.Fragment>
                {checkFile && fileDetail && <UploadFileData data={fileDetail} control={control} errors={errors} />}
              </React.Fragment>
            )}
          </Flex>
        )}
      </Form>
      <Flex
        style={{
          justifyContent: 'flex-end',
          paddingRight: 24,
        }}
        className='button-group-container'
      >
        <Button
          type='primary'
          className='new-account-button'
          htmlType='button'
          disabled={isDisableButton || isPurging}
          onClick={() => setIsOpenDeleteModal(true)}
          loading={isPurging}
          style={{
            width: 168,
            display: purgeTypeWatch === IMPORT_PURGE_TYPE.ALL_TRANSACTION_HISTORY ? 'none' : 'block',
          }}
        >
          {t('PurgeTransactions:deleteTransactions')}
        </Button>
      </Flex>
      <Modal
        isOpen={isShowModal}
        getValues={getValues}
        onClose={() => setIsShowModal(false)}
        isWarning={false}
        hasIconWarning={false}
        onConfirm={() => {
          if (getValues('accountName') !== DEFAULT_ALL_ACCOUNTS.value) {
            setIsOpenDeleteModal(true)
            setIsShowModal(false)
          }
        }}
      />
      <DeleteTransactionsModal
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal(false)}
        onConfirm={handleSubmit(onSubmit)}
        data={{ numberOfRecordsToDelete: fileDetail?.numOfRecords, totalAmount: fileDetail?.totalAmount }}
        fileName={`${fileDetail?.fileName} - ${fileDetail?.dateUploaded}`}
        isPurging={isPurging}
      />
    </Layout.Content>
  )
}

export default PurgeTransactions
